import { routes } from '../routes/routePaths';
import type { AppConfig } from '../types/config.types';

export const DASHBOARD_STATUS_CARD_ORDER = [
  'REV',
  'PEN',
  'APP',
  'COM',
  'INS',
  'RTI',
] as const;

export type DashboardStatusCode = (typeof DASHBOARD_STATUS_CARD_ORDER)[number];

export type DashboardLayoutMode = 'status' | 'module';

export type DashboardStatusCardConfig = {
  code: DashboardStatusCode;
  label: string;
  route: string;
  iconName: string;
  iconMsg: string;
};

const DASHBOARD_STATUS_CARD_CONFIG: Record<
  DashboardStatusCode,
  DashboardStatusCardConfig
> = {
  REV: {
    code: 'REV',
    label: 'Por Revisar',
    route: routes.dashboard,
    iconName: 'origination_outline',
    iconMsg: 'Por Revisar',
  },
  PEN: {
    code: 'PEN',
    label: 'Pend. del Cliente',
    route: routes.dashboard,
    iconName: 'clock_outline',
    iconMsg: 'Pend. del Cliente',
  },
  APP: {
    code: 'APP',
    label: 'En Análisis de Riesgo',
    route: routes.risksAnalysis,
    iconName: 'risk_outline',
    iconMsg: 'En Análisis de Riesgo',
  },
  COM: {
    code: 'COM',
    label: 'En Comité',
    route: routes.committee,
    iconName: 'committee_outline',
    iconMsg: 'En Comité',
  },
  INS: {
    code: 'INS',
    label: 'Pendiente de Firma',
    route: routes.instrumentation,
    iconName: 'instrumentation_outline',
    iconMsg: 'Pendiente de Firma',
  },
  RTI: {
    code: 'RTI',
    label: 'Cliente Activo',
    route: routes.instrumentation,
    iconName: 'user_outline',
    iconMsg: 'Cliente Activo',
  },
};

const DASHBOARD_MODULE_LABELS: Partial<Record<DashboardStatusCode, string>> = {
  REV: 'Originación',
  APP: 'Análisis de Riesgo',
  COM: 'Comité',
  INS: 'Instrumentación',
};

export type DashboardLayout = {
  mode: DashboardLayoutMode;
  cardOrder: readonly string[];
  showPendingSubtitle: boolean;
};

export const resolveDashboardLayout = (
  config: Pick<AppConfig, 'isDashboardStatusCountEnabled' | 'isInstrumentationEnabled'>,
): DashboardLayout => {
  if (config.isDashboardStatusCountEnabled === true) {
    return {
      mode: 'status',
      cardOrder: DASHBOARD_STATUS_CARD_ORDER,
      showPendingSubtitle: false,
    };
  }

  const cardOrder: string[] = ['REV', 'APP', 'COM'];
  if (config.isInstrumentationEnabled !== false) {
    cardOrder.push('INS');
  }

  return {
    mode: 'module',
    cardOrder,
    showPendingSubtitle: true,
  };
};

export const getDashboardStatusCardConfig = (
  code: string,
  mode: DashboardLayoutMode = 'status',
): DashboardStatusCardConfig | undefined => {
  const base = DASHBOARD_STATUS_CARD_CONFIG[code as DashboardStatusCode];
  if (!base) {
    return undefined;
  }

  if (mode === 'module') {
    const moduleLabel = DASHBOARD_MODULE_LABELS[code as DashboardStatusCode];
    if (moduleLabel) {
      return { ...base, label: moduleLabel, iconMsg: moduleLabel };
    }
  }

  return base;
};

export const getOrderedDashboardStatusCards = (): DashboardStatusCardConfig[] =>
  DASHBOARD_STATUS_CARD_ORDER.map((code) => DASHBOARD_STATUS_CARD_CONFIG[code]);

export type DashboardStatsSection = {
  code: string;
  pending: number;
};

export const mergeDashboardStatusSections = (
  sections: DashboardStatsSection[] | undefined | null,
  cardOrder: readonly string[] = DASHBOARD_STATUS_CARD_ORDER,
): DashboardStatsSection[] => {
  const countsByCode = new Map(
    (Array.isArray(sections) ? sections : []).map((section) => [
      section.code,
      section.pending ?? 0,
    ]),
  );

  return cardOrder.map((code) => ({
    code,
    pending: countsByCode.get(code) ?? 0,
  }));
};
